/**
 * musicTheory.ts
 *
 * Guitar music theory utilities: chromatic scale, named scales,
 * tuning definitions, and fretboard note mapping.
 */

/* ------------------------------------------------------------------ */
/*  Chromatic notes                                                    */
/* ------------------------------------------------------------------ */

/** All 12 chromatic pitch classes (sharps only, enharmonic-agnostic) */
export const CHROMATIC_NOTES = [
  'C',
  'C#',
  'D',
  'D#',
  'E',
  'F',
  'F#',
  'G',
  'G#',
  'A',
  'A#',
  'B',
] as const;

export type NoteName = (typeof CHROMATIC_NOTES)[number];

/** Convert a note name (including flats) to its semitone index 0-11 */
export function noteToSemitone(note: string): number {
  const map: Record<string, number> = {
    C: 0,
    'C#': 1,
    Db: 1,
    D: 2,
    'D#': 3,
    Eb: 3,
    E: 4,
    Fb: 4,
    F: 5,
    'E#': 5,
    'F#': 6,
    Gb: 6,
    G: 7,
    'G#': 8,
    Ab: 8,
    A: 9,
    'A#': 10,
    Bb: 10,
    B: 11,
    Cb: 11,
  };
  return map[note] ?? 0;
}

/** Convert semitone index 0-11 to a note name */
export function semitoneToNote(semitone: number): NoteName {
  return CHROMATIC_NOTES[((semitone % 12) + 12) % 12];
}

/* ------------------------------------------------------------------ */
/*  Scale definitions                                                  */
/* ------------------------------------------------------------------ */

/** Interval patterns as semitone offsets from root */
export const SCALE_INTERVALS: Record<string, number[]> = {
  // Major modes
  'Major (Ionian)': [0, 2, 4, 5, 7, 9, 11],
  Dorian: [0, 2, 3, 5, 7, 9, 10],
  Phrygian: [0, 1, 3, 5, 7, 8, 10],
  Lydian: [0, 2, 4, 6, 7, 9, 11],
  Mixolydian: [0, 2, 4, 5, 7, 9, 10],
  'Minor (Aeolian)': [0, 2, 3, 5, 7, 8, 10],
  Locrian: [0, 1, 3, 5, 6, 8, 10],

  // Pentatonics
  'Major Pentatonic': [0, 2, 4, 7, 9],
  'Minor Pentatonic': [0, 3, 5, 7, 10],

  // Blues
  'Blues (major)': [0, 2, 3, 4, 7, 9],
  'Blues (minor)': [0, 3, 5, 6, 7, 10],
  // Aliases used by the blues / diatonic position trainers so the
  // live fretboard scale overlay resolves to the right intervals.
  'Major Blues': [0, 2, 3, 4, 7, 9],
  'Minor Blues': [0, 3, 5, 6, 7, 10],
  'Major Scale': [0, 2, 4, 5, 7, 9, 11],
  'Minor Scale': [0, 2, 3, 5, 7, 8, 10],

  // Harmonic & melodic
  'Harmonic Minor': [0, 2, 3, 5, 7, 8, 11],
  'Melodic Minor': [0, 2, 3, 5, 7, 9, 11],

  // Exotic
  'Whole Tone': [0, 2, 4, 6, 8, 10],
  Diminished: [0, 2, 3, 5, 6, 8, 9, 11],
  Chromatic: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
};

export const SCALE_NAMES = Object.keys(SCALE_INTERVALS);

/** Get all notes in a given scale starting from a root */
export function getScaleNotes(root: NoteName, scaleName: string): NoteName[] {
  const intervals = SCALE_INTERVALS[scaleName];
  if (!intervals) return [];
  const rootSemitone = noteToSemitone(root);
  return intervals.map((i) => semitoneToNote(rootSemitone + i));
}

/** Check if a note belongs to a scale */
export function isInScale(
  note: NoteName,
  root: NoteName,
  scaleName: string
): boolean {
  const scaleNotes = getScaleNotes(root, scaleName);
  return scaleNotes.includes(note);
}

/* ------------------------------------------------------------------ */
/*  Interval classification                                            */
/* ------------------------------------------------------------------ */

export type IntervalRole =
  | 'root'
  | 'minor2nd'
  | 'major2nd'
  | 'minor3rd'
  | 'major3rd'
  | 'perfect4th'
  | 'tritone'
  | 'perfect5th'
  | 'minor6th'
  | 'major6th'
  | 'minor7th'
  | 'major7th'
  | 'nonScale';

const INTERVAL_NAMES: Record<number, IntervalRole> = {
  0: 'root',
  1: 'minor2nd',
  2: 'major2nd',
  3: 'minor3rd',
  4: 'major3rd',
  5: 'perfect4th',
  6: 'tritone',
  7: 'perfect5th',
  8: 'minor6th',
  9: 'major6th',
  10: 'minor7th',
  11: 'major7th',
};

/** Get the interval role of a note relative to a root */
export function getIntervalRole(
  note: NoteName,
  root: NoteName,
  scaleName: string
): IntervalRole {
  if (!isInScale(note, root, scaleName)) return 'nonScale';
  const semitones =
    (((noteToSemitone(note) - noteToSemitone(root)) % 12) + 12) % 12;
  return INTERVAL_NAMES[semitones] ?? 'nonScale';
}

/** Human-readable interval labels */
export const INTERVAL_LABELS: Record<IntervalRole, string> = {
  root: 'Root',
  minor2nd: 'b2',
  major2nd: '2nd',
  minor3rd: 'b3',
  major3rd: '3rd',
  perfect4th: '4th',
  tritone: 'b5',
  perfect5th: '5th',
  minor6th: 'b6',
  major6th: '6th',
  minor7th: 'b7',
  major7th: '7th',
  nonScale: '',
};

/* ------------------------------------------------------------------ */
/*  Tuning presets                                                     */
/* ------------------------------------------------------------------ */

export interface GuitarTuning {
  name: string;
  notes: NoteName[];
}

export const TUNING_PRESETS: GuitarTuning[] = [
  { name: 'Standard', notes: ['E', 'A', 'D', 'G', 'B', 'E'] },
  { name: 'Drop D', notes: ['D', 'A', 'D', 'G', 'B', 'E'] },
  { name: 'Half-step down', notes: ['D#', 'G#', 'C#', 'F#', 'A#', 'D#'] },
  { name: 'Whole-step down', notes: ['D', 'G', 'C', 'F', 'A', 'D'] },
  { name: 'Drop C', notes: ['C', 'G', 'C', 'F', 'A', 'D'] },
  { name: 'Open G', notes: ['D', 'G', 'D', 'G', 'B', 'D'] },
  { name: 'Open D', notes: ['D', 'A', 'D', 'F#', 'A', 'D'] },
  { name: 'Open E', notes: ['E', 'B', 'E', 'G#', 'B', 'E'] },
  { name: 'DADGAD', notes: ['D', 'A', 'D', 'G', 'A', 'D'] },
  { name: 'Open C', notes: ['C', 'G', 'C', 'G', 'C', 'E'] },
];

/* ------------------------------------------------------------------ */
/*  Fretboard note mapping                                             */
/* ------------------------------------------------------------------ */

export interface FretNote {
  /** String index (0 = lowest pitched / thickest) */
  string: number;
  /** Fret number (0 = open) */
  fret: number;
  /** Pitch class */
  note: NoteName;
  /** Semitone offset from root of the current scale */
  semitoneFromRoot: number;
  /** Role within the current scale */
  intervalRole: IntervalRole;
  /** Whether this note is in the selected scale */
  inScale: boolean;
}

/**
 * Build a complete fret→note map for the given tuning, scale, and fret count.
 * Returns a 2D array: [stringIndex][fretIndex] → FretNote
 */
export function buildFretboardMap(
  tuning: NoteName[],
  root: NoteName,
  scaleName: string,
  fretCount: number
): FretNote[][] {
  const rootSemitone = noteToSemitone(root);
  const scaleNotes = getScaleNotes(root, scaleName);

  return tuning.map((openNote, stringIdx) => {
    const openSemitone = noteToSemitone(openNote);
    return Array.from({ length: fretCount + 1 }, (_, fret) => {
      const semitone = (openSemitone + fret) % 12;
      const note = semitoneToNote(semitone);
      const inScale = scaleNotes.includes(note);
      const semitoneFromRoot = (((semitone - rootSemitone) % 12) + 12) % 12;
      const intervalRole = inScale
        ? INTERVAL_NAMES[semitoneFromRoot] ?? 'nonScale'
        : 'nonScale';

      return {
        string: stringIdx,
        fret,
        note,
        semitoneFromRoot,
        intervalRole,
        inScale,
      };
    });
  });
}

/* ------------------------------------------------------------------ */
/*  Fretboard note at position                                         */
/* ------------------------------------------------------------------ */

/**
 * Compute the note name at a given fret / string position
 * relative to a tuning array (low→high).
 */
export function getNoteAtPos(
  fret: number,
  stringIdx: number,
  tuning: NoteName[]
): NoteName {
  const open = noteToSemitone(tuning[stringIdx] ?? 'E');
  return semitoneToNote((open + fret) % 12);
}

/**
 * Frequency of a note at a given octave.
 * A4 = 440 Hz. Middle C = C4 ≈ 261.63 Hz.
 */
export function noteFrequency(note: NoteName, octave: number): number {
  const semitone = noteToSemitone(note);
  // A4 = 440Hz, A is semitone 9
  const halfStepsFromA4 = semitone - 9 + (octave - 4) * 12;
  return 440 * Math.pow(2, halfStepsFromA4 / 12);
}

/**
 * Given a frequency, find the closest note name and how many cents off it is.
 */
export function frequencyToNote(
  freq: number
): { note: NoteName; octave: number; cents: number } | null {
  if (freq <= 0 || !isFinite(freq)) return null;
  const halfStepsFromA4 = 12 * Math.log2(freq / 440);
  const rounded = Math.round(halfStepsFromA4);
  const cents = (halfStepsFromA4 - rounded) * 100;
  const semitone = (((rounded + 9) % 12) + 12) % 12;
  const octave = 4 + Math.floor((rounded + 9) / 12);
  return { note: semitoneToNote(semitone), octave, cents };
}
